import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from "recharts";

type Top10Item = {
  name: string;
  count: number;
  percent: number;
  color?: string;
};

function fmtPercent(v: number) {
  if (!isFinite(v)) return "0.0%";
  return `${v.toFixed(1)}%`;
}

/**
 * Top 10 categories (horizontal bar chart).
 * - Reveals bars from top to bottom WITHOUT using animationBegin (avoids TS errors).
 * - Uses currentColor so text follows light/dark mode from wrapper.
 */
export function CategoryTop10Bar({
  data,
  noDataLabel,
}: {
  data: Top10Item[];
  noDataLabel: string;
}) {
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;
  const hasData = Array.isArray(data) && data.length > 0;
  const sourceData = hasData ? data : [];

  // Respect reduced motion
  const reduceMotion = useMemo(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  // Reveal bars from top to bottom
  const [visibleCount, setVisibleCount] = useState<number>(
    reduceMotion ? sourceData.length : 0
  );

  useEffect(() => {
    if (!hasData) {
      setVisibleCount(0);
      return;
    }

    if (reduceMotion) {
      setVisibleCount(sourceData.length);
      return;
    }

    // Restart animation whenever data changes
    setVisibleCount(0);

    const STAGGER_MS = 90; // controls "top-to-bottom" speed
    let i = 0;

    const id = window.setInterval(() => {
      i += 1;
      setVisibleCount(i);
      if (i >= sourceData.length) window.clearInterval(id);
    }, STAGGER_MS);

    return () => window.clearInterval(id);
  }, [hasData, sourceData.length, reduceMotion]);

  // Build chart data: unrevealed bars stay at 0
  const chartData = useMemo(() => {
    if (reduceMotion) return sourceData;
    return sourceData.map((d, idx) => ({
      ...d,
      count: idx < visibleCount ? d.count : 0,
    }));
  }, [sourceData, visibleCount, reduceMotion]);

  if (!hasData) {
    return (
      <div className="text-sm" style={secondaryTextStyle}>
        {noDataLabel}
      </div>
    );
  }

  // Tune the smoothness of each step
  const STEP_ANIM_MS = 350;

  return (
    <div className="w-full" style={primaryTextStyle}>
      <div style={{ height: 360, width: "100%" }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
          >
            <XAxis type="number" hide />

            <YAxis
              type="category"
              dataKey="name"
              width={175}
              tick={{ fill: "currentColor", fontSize: 13, fontWeight: 500 }}
              axisLine={false}
              tickLine={false}
            />

            <Tooltip
              cursor={{ fill: "var(--muted)" }}
              formatter={(value: any, _name: any, item: any) => {
                const p = item?.payload as Top10Item | undefined;
                const pct = p?.percent ?? 0;
                return [`${Number(value)} (${fmtPercent(Number(pct))})`, ""];
              }}
              contentStyle={{
                borderRadius: 10,
                border: "1px solid var(--border)",
                background: "var(--card)",
                color: "var(--foreground)",
                fontSize: 12,
              }}
              wrapperStyle={{ outline: "none" }}
            />

            <Bar
              dataKey="count"
              radius={[6, 6, 6, 6]}
              isAnimationActive={!reduceMotion}
              animationDuration={STEP_ANIM_MS}
              animationEasing="ease-in-out"
              label={{
                position: "right",
                fill: "currentColor",
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {chartData.map((entry) => (
                <Cell
                  key={entry.name}
                  fill={entry.color ?? "var(--category-scale-3)"}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
